// École Nid Douillet - Public Layout Component
// Layout for public website pages

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PublicLayoutProps {
  children: React.ReactNode;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between items-center">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-primary-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">ND</span>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-neutral-900">
                  École Nid Douillet
                </h1>
              </div>
            </div>

            {/* Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className="text-neutral-700 hover:text-primary-600 px-3 py-2 text-sm font-medium"
              >
                Accueil
              </Link>
              <Link
                to="/a-propos"
                className="text-neutral-700 hover:text-primary-600 px-3 py-2 text-sm font-medium"
              >
                À propos
              </Link>
              <Link
                to="/news"
                className="text-neutral-700 hover:text-primary-600 px-3 py-2 text-sm font-medium"
              >
                Actualités
              </Link>
              <Link
                to="/inscriptions"
                className="text-neutral-700 hover:text-primary-600 px-3 py-2 text-sm font-medium"
              >
                Inscription
              </Link>
              <Link
                to="/contact"
                className="text-neutral-700 hover:text-primary-600 px-3 py-2 text-sm font-medium"
              >
                Contact
              </Link>
              <Link
                to="/auth/login"
                className="bg-primary-600 text-white hover:bg-primary-700 px-4 py-2 rounded-lg text-sm font-medium"
              >
                Connexion
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Main content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* School info */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">ND</span>
                </div>
                <h3 className="text-lg font-semibold">École Nid Douillet</h3>
              </div>
              <p className="text-neutral-300 text-sm">
                {t('school.description')}
              </p>
              <p className="text-neutral-400 text-sm mt-2">
                {t('school.motto')}
              </p>
            </div>

            {/* Contact info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <div className="space-y-2 text-sm text-neutral-300">
                <p>{t('school.address')}</p>
                <p>{t('school.phone')}</p>
                <p>{t('school.email')}</p>
              </div>
            </div>

            {/* Quick links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Liens rapides</h3>
              <div className="space-y-2">
                <Link
                  to="/public/enrollment"
                  className="block text-sm text-neutral-300 hover:text-white"
                >
                  Inscription en ligne
                </Link>
                <Link
                  to="/public/about"
                  className="block text-sm text-neutral-300 hover:text-white"
                >
                  Notre pédagogie
                </Link>
                <Link
                  to="/auth/login"
                  className="block text-sm text-neutral-300 hover:text-white"
                >
                  Espace parents
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-neutral-800 text-center">
            <p className="text-sm text-neutral-400">
              © 2024 École Maternelle Nid Douillet. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
